import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { BankingData } from "./banking-certification.interface";
import { DialogService } from "src/app/services/dialog.service";
import { CustomValidators } from "src/app/proveiders/CustomValidators";
import { Documento } from "src/app/models/documento";
import { DocumentExtractionService } from "src/app/services/banking-certification.service";
import { MiPerfilService } from "src/app/services/mi-perfil/mi-perfil.service";
import { CatalogParamsService } from "@modules/shared/services/catalog-params.service";
import { AdicionalesService } from "src/app/services/catalogos/adicionales.service";
import { growVertical } from "src/app/animations";

@Component({
  selector: "banking-certification",
  templateUrl: "./banking-certification.component.html",
  styleUrls: ["../mi-perfil-page.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ growVertical ]
})
export class BankingCertificationComponent implements OnInit, OnChanges {
  @Input() initialData: BankingData = {
    banco: "",
    tipoCuenta: "",
    numeroCuenta: "",
  };
  @Input() isUpdate: boolean = false;
  @Output() save = new EventEmitter<BankingData>();

  public formInfoBancaria: UntypedFormGroup;
  colorAdvertencia: "success" | "warning" | "info" = "success";
  textoAdvertencia = "";
  showAdvertencia: boolean = false;
  loadingAdvertencia = false;
  public documento: Documento = {
    archivo: null,
    nombre: "",
    status: false,
    upload: false,
    secuencia: 0,
  };
  public rules: string[] = ["El archivo que selecciones no debe superar los <strong>5MB</strong>.", "Debe estar en formato <strong>PDF.</strong>","<strong>El documento no debe tener más de 30 días de expedido.<strong>"];

  public bayportInfoForm: UntypedFormGroup = new UntypedFormGroup({
    executiveCode: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.required,
    ]),
    executiveType: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.required,
    ]),
    creationDate: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.required,
    ]),
    state: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.required,
    ]),
    regional: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.required,
    ]),
    branch: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.required,
    ]),
    commertialCoordinator: new UntypedFormControl(
      { value: "", disabled: true },
      [Validators.required]
    ),
    bayportEmail: new UntypedFormControl({ value: "", disabled: false }, [
      Validators.compose([Validators.required, Validators.email]),
    ]),
    correo2: new UntypedFormControl({ value: "", disabled: false }, [
      Validators.compose([Validators.required, Validators.email]),
    ]),
    channelType: new UntypedFormControl({ value: 1, disabled: true }, [
      Validators.required,
    ]),
    salesChannel: new UntypedFormControl({ value: 1, disabled: true }, [
      Validators.required,
    ]),
    celular: new UntypedFormControl({ disabled: false }, [
      Validators.required,
      Validators.maxLength(10),
      Validators.minLength(10),
      CustomValidators.patternValidator(/^3/i, { numberThree: true }),
      Validators.pattern(/^[0-9]+$/i),
    ]),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private documentExtractService: DocumentExtractionService,
    private dialogService: DialogService,
    private perfilService: MiPerfilService,
    private _paramsService: CatalogParamsService,
    private _additionalsServices: AdicionalesService,
    private cd: ChangeDetectorRef
  ) {
    this.formInfoBancaria = this.fb.group({
      banco: [{ value: "", disabled: true }, Validators.required],
      tipoCuenta: [{ value: "", disabled: true }, Validators.required],
      numeroCuenta: [{ value: "", disabled: true }, Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    this.formInfoBancaria.patchValue(this.initialData);

    await this.getCatalogs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialData && !changes.initialData.firstChange) {
      this.formInfoBancaria.patchValue(changes.initialData.currentValue);
    }
  }

  private async getCatalogs(): Promise<void> {
    await this.getBanks();
    this.getAccountType();
  }

  actualizarInformacionBancaria(): void {
    this.isUpdate = true;
  }

  onFileChange(event: any): void {
    const file: File = event.target ? event.target.files[0] : event;
    if (!file) return;

    this.documento.archivo = file;
    this.documento.nombre = file.name;
    this.documento.status = true;

    this.dialogService.openDialogLoadingService("");

    const token = JSON.parse(localStorage.getItem("credentials")).access_token;

    this.documentExtractService.uploadDocument(file, token).subscribe(
      (response: any) => {
        const { nombreBanco, tipoCuenta, numeroCuenta } = response;
        this.formInfoBancaria.controls.banco.setValue(nombreBanco);
        this.formInfoBancaria.controls.tipoCuenta.setValue(tipoCuenta);
        this.formInfoBancaria.controls.numeroCuenta.setValue(numeroCuenta);

        this.dialogService.closeDialog();
        this.save.emit(this.formInfoBancaria.getRawValue());
      },
      (error: any) => {
        this.fallaPeticion(
          error.error.message || "Ocurrió un error inesperado."
        );
      }
    );
  }

  public banks: Array<{ codigo: string; nombre: string }> = [];
  public accountTypes: Array<{ codigo: string; nombre: string }> = [];

  private async getBanks(): Promise<void> {
    try {
      const res = await this._paramsService.getBanks().toPromise();
      console.log({ res });
      this.banks = res.rows;
    } catch (error) {
      console.error(error.error.message);
    }
  }

  private getAccountType(): void {
    this._additionalsServices.getTipoCuenta().subscribe(
      (res) => {
        this.accountTypes = res.rows.map((r) => ({
          codigo: String(r.codigo),
          nombre: r.nombre.trim(),
        }));
      },
      (err) => console.error(err)
    );
  }

  upload(): void {
    if (!this.documento.archivo) {
      this.fallaPeticion("No se seleccionó ningún archivo");
      return;
    }

    this.dialogService.openDialogLoadingService();

    const token = JSON.parse(localStorage.getItem("credentials")).access_token;

    this.documentExtractService
      .uploadDocument(this.documento.archivo, token)
      .subscribe(
        (response) => {
          this.dialogService.closeDialog();
          const cr = (response as any).carresponse;
          if (cr?.codigo === 200) {
            this.confirmarActualizacion(cr, token);
          } else {
            this.fallaPeticion(
              response.message || "Ocurrió un error al procesar el documento"
            );
            this.dialogService.openDialog(
              "Error",
              (response as any).message ||
                "Ocurrió un error al procesar el documento",
              false
            );
          }
        },
        (error) => {
          this.dialogService.closeDialog();
          this.fallaPeticion(
            error.error?.message || "Ocurrió un error inesperado"
          );
        }
      );
  }

  private confirmarActualizacion(cr: any, token: string) {
    this.loadingAdvertencia = true;
    const bancoResp = cr.nombreBanco?.trim().toLowerCase();
    let tipoResp = cr.tipoCuenta?.trim().toLowerCase();
    console.log("RESPUESTA OCR:", bancoResp, tipoResp);

    if (!bancoResp || !tipoResp) {
      const text = "Respuesta incompleta (falta banco o tipo de cuenta)";
      this.fallaPeticion(text);
    }

    const bankEntry = this.banks.find(
      (b) => b.nombre.trim().toLowerCase() === bancoResp
    );
    console.log("SELECCIONADO bankEntry:", bankEntry);

    let typeEntry = this.accountTypes.find(
      (a) => a.nombre.trim().toLowerCase() === tipoResp
    );

    if (!typeEntry) {
      const withS = tipoResp.endsWith("s") ? tipoResp : tipoResp + "s";
      const withoutS = tipoResp.endsWith("s")
        ? tipoResp.slice(0, -1)
        : tipoResp;

      typeEntry = this.accountTypes.find(
        (a) =>
          a.nombre.trim().toLowerCase() === withS ||
          a.nombre.trim().toLowerCase() === withoutS
      );
      console.log("FALLBACK tipoCuenta conS/sinS:", withS, withoutS, typeEntry);
    } else {
      console.log("SELECCIONADO typeEntry:", typeEntry);
    }

    if (!bankEntry || !typeEntry) {
      const text = "No se encontró el banco o el tipo de cuenta en el catálogo";
      this.fallaPeticion(text);
    }

    const infoFinanciera = {
      entidad_financiera: bankEntry.codigo,
      tipo_cuenta: typeEntry.codigo,
      numero_cuenta: cr.numeroCuenta,
      num_identificacion_asesor: cr.numeroCedula,
      token_usuario: token,
    };

    this.perfilService.updateInfoBank(infoFinanciera).subscribe(
      () => {
        this.textoAdvertencia =
          "Información financiera actualizada correctamente";
        this.exitoPeticion();
        this.save.emit({
          banco: cr.nombreBanco,
          tipoCuenta: cr.tipoCuenta,
          numeroCuenta: cr.numeroCuenta,
        });
      },
      (err) => {
        this.fallaPeticion(
          err.error?.message || "Fallo al actualizar información financiera"
        );
      }
    );
  }

  private formatResponse(response: any): string {
    const { cdstatus, carresponse, alert } = response;
    const mapStatus = (s: string) => ({ A: "Activo", I: "Inactivo" }[s] ?? s);

    let html = `
      <table style="width:100%; border-collapse:collapse; font-family:Arial,sans-serif;">
        <tr>
          <th style="text-align:left; padding:6px; border-bottom:1px solid #ccc;">📄 Estado</th>
          <td style="padding:6px; border-bottom:1px solid #ccc;">${mapStatus(
            cdstatus
          )}</td>
        </tr>`;

    if (carresponse) {
      html += `
        <tr>
          <th style="text-align:left; padding:6px; border-bottom:1px solid #ccc;" colspan="2">🏦 Datos bancarios</th>
        </tr>
        <tr><td style="padding:6px; border-bottom:1px solid #eee;">Banco:</td><td style="padding:6px; border-bottom:1px solid #eee;">${carresponse.nombreBanco}</td></tr>
        <tr><td style="padding:6px; border-bottom:1px solid #eee;">Fecha exp:</td><td style="padding:6px; border-bottom:1px solid #eee;">${carresponse.fechaExpedicion}</td></tr>
        <tr><td style="padding:6px; border-bottom:1px solid #eee;">Cédula:</td><td style="padding:6px; border-bottom:1px solid #eee;">${carresponse.numeroCedula}</td></tr>
        <tr><td style="padding:6px; border-bottom:1px solid #eee;">Titular:</td><td style="padding:6px; border-bottom:1px solid #eee;">${carresponse.nombrePersona}</td></tr>
        <tr><td style="padding:6px; border-bottom:1px solid #eee;">Tipo cuenta:</td><td style="padding:6px; border-bottom:1px solid #eee;">${carresponse.tipoCuenta}</td></tr>
        <tr><td style="padding:6px; border-bottom:1px solid #eee;">Núm. cuenta:</td><td style="padding:6px; border-bottom:1px solid #eee;">${carresponse.numeroCuenta}</td></tr>
        <tr><td style="padding:6px; border-bottom:1px solid #eee;">Estado cuenta:</td><td style="padding:6px; border-bottom:1px solid #eee;">${carresponse.estadoCuenta}</td></tr>`;
    }

    if (alert) {
      html += `
        <tr>
          <th style="text-align:left; padding:6px; border-bottom:1px solid #ccc;">⚠️ Alerta</th>
          <td style="padding:6px; border-bottom:1px solid #ccc;">${alert}</td>
        </tr>`;
    }

    html += `</table>`;
    return html;
  }

  exitoPeticion() {
    this.colorAdvertencia = "success";
    this.textoAdvertencia = "La información fue guardada exitosamente";
    this.showAdvertencia = true;
    this.loadingAdvertencia = false;
    setTimeout(() => {
      this.showAdvertencia = false;
    }, 5000); // 5 segundos
  }

  fallaPeticion(text: string) {
    this.colorAdvertencia = "warning";
    this.textoAdvertencia = text;
    this.showAdvertencia = true;
    this.loadingAdvertencia = false;
    this.cd.detectChanges();
    setTimeout(() => {
      this.cd.detectChanges();
      this.showAdvertencia = false;
    }, 5000); // 5 segundos
  }
}
